import Enrollments from '@/services/Enrollments';
import axios from 'axios';
import SentryClient from '../sentry';
import {
  TrackContentOpenedType,
  TrackContentConsumedType,
  TrackCourseProgressType,
  TrackDisciplineProgressType,
  TrackLessonPlayedType,
  TrackLessonReactedType,
  TrackForumChannelOpenedType,
  TrackForumTopicCreated,
  TrackForumCommentPostedType,
  TrackPageViewType,
  TrackNotificationInterectedType,
  TrackNotificationViewedType,
  TrackStudyCaseSentType,
  TrackAssessmentResultViewType,
  TrackPitchSentType,
  TrackPostgradStudyPlanningModalType,
  TrackPostgradStudyPlanningMonthsOptionType,
  TrackPostgradStudyPlanningLessonClickType,
  TrackStudyCaseModalView,
  TrackDexAssessmentModalView,
  TrackPitchModalView,
  TrackPrivacyPolicyType,
  TrackSmartBannerType,
  TrackStudyPlanInteracted,
  TrackTermsOfUse,
  TrackOverdueUserWarningModalInteraction,
  TrackOverdueUserWarningModalViewed,
  TrackMicrocertificateInteraction,
  TrackCompleteWeeklyListsCardInteraction,
  TrackPostgradMicrocertificateError,
  TrackPostgradMicrocertificateDownloadButtonClick,
  TrackPostgradMicrocertificateProgressBarClick,
  TrackMenuClicked,
  TrackTooltipViewed,
  TrackDownloadFinished,
  TrackPostgradMicrocertificateNameCheckingModalViewed,
  TrackPostgradMicrocertificateNameCheckingModalInteraction,
  TrackPostgradMicrocertificateLoadingModalInteraction,
  TrackPostgradMicrocertificateSharingAndDownloadModalInteraction,
  TrackPostgradMicrocertificateInvalidNameModalInteraction,
  TrackReportCardToggle,
  TrackReportCardView,
  TrackReportCardActivityClick,
  TrackAccessibilityIconClick,
  TrackHelpCenterArticles,
  TrackPostgradContractExtensionRequestRegistered,
  TrackPostgradContractExtensionPageExtensionButtonClick,
  TrackPostgradContractExtensionPageLandingsButtonClick,
  TrackPostgradContractExtensionPageCoursesButtonClick,
  TrackPostgradContractExtensionPageSignOutButtonClick,
  TrackExpiredPostgradContractExtensionPageView,
  TrackFinishingCourseExpiredContractPageView,
  TrackBaseExpiredPostgradContractPageView,
  TrackPostgradContractExtensionButtonVisible,
  TrackTrialInteraction,
} from './types';

type TrackData =
  | TrackContentConsumedType
  | TrackContentOpenedType
  | TrackCourseProgressType
  | TrackDisciplineProgressType
  | TrackLessonPlayedType
  | TrackLessonReactedType
  | TrackForumChannelOpenedType
  | TrackForumTopicCreated
  | TrackForumCommentPostedType
  | TrackPageViewType
  | TrackNotificationViewedType
  | TrackNotificationInterectedType
  | TrackStudyCaseSentType
  | TrackPitchSentType
  | TrackAssessmentResultViewType
  | TrackPostgradStudyPlanningModalType
  | TrackPostgradStudyPlanningMonthsOptionType
  | TrackPostgradStudyPlanningLessonClickType
  | TrackStudyCaseModalView
  | TrackDexAssessmentModalView
  | TrackPitchModalView
  | TrackPrivacyPolicyType
  | TrackSmartBannerType
  | TrackStudyPlanInteracted
  | TrackTermsOfUse
  | TrackOverdueUserWarningModalInteraction
  | TrackOverdueUserWarningModalViewed
  | TrackMicrocertificateInteraction
  | TrackCompleteWeeklyListsCardInteraction
  | TrackPostgradMicrocertificateError
  | TrackPostgradMicrocertificateDownloadButtonClick
  | TrackPostgradMicrocertificateProgressBarClick
  | TrackPostgradMicrocertificateNameCheckingModalViewed
  | TrackPostgradMicrocertificateNameCheckingModalInteraction
  | TrackPostgradMicrocertificateInvalidNameModalInteraction
  | TrackMenuClicked
  | TrackTooltipViewed
  | TrackDownloadFinished
  | TrackPostgradMicrocertificateLoadingModalInteraction
  | TrackPostgradMicrocertificateSharingAndDownloadModalInteraction
  | TrackReportCardToggle
  | TrackReportCardView
  | TrackReportCardActivityClick
  | TrackAccessibilityIconClick
  | TrackHelpCenterArticles
  | TrackBaseExpiredPostgradContractPageView
  | TrackPostgradContractExtensionButtonVisible
  | TrackExpiredPostgradContractExtensionPageView
  | TrackFinishingCourseExpiredContractPageView
  | TrackPostgradContractExtensionPageExtensionButtonClick
  | TrackPostgradContractExtensionPageLandingsButtonClick
  | TrackPostgradContractExtensionRequestRegistered
  | TrackPostgradContractExtensionPageCoursesButtonClick
  | TrackPostgradContractExtensionPageSignOutButtonClick
  | TrackTrialInteraction;

interface UserIdentify {
  id: number;
  email: string;
  name: string;
  flags?: { [key: string]: string | boolean };
  courses?: string[];
  categories?: string[];
}

interface PocketOptions {
  useMixpanel: boolean;
  useHubspot: boolean;
}

interface UserCourse {
  name: string | null;
  category: string | null;
}

class Analytics {
  static identify(
    data: UserIdentify,
    options: PocketOptions = { useHubspot: true, useMixpanel: true },
  ) {
    const { id, email, name, flags, courses, categories } = data;
    const storedProfile = window.POCKET.profile() || {};
    const userProfile = {
      id: storedProfile.id || id,
      email: storedProfile.email || email,
      name: storedProfile.name || name || 'Aluno do Descomplica',
    };

    window.POCKET.identify(
      userProfile.id.toString(),
      {
        email: userProfile.email,
        name: userProfile.name,
        $email: userProfile.email,
        courses: courses || [],
        categories: categories || [],
        ...flags,
      },
      options,
    );
  }

  static updateUserCourses(course: UserCourse) {
    try {
      const storedProfile = window.POCKET.profile() || {};

      window.POCKET.identify(storedProfile.id.toString(), {
        course: course.name,
        category: course.category,
        ...storedProfile,
      });
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static trackEvent<TProperties = TrackData>(
    eventName: string,
    trackData = {} as TProperties,
  ): void {
    try {
      window.POCKET.track(eventName, trackData as Object);
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static trackTimeEvent(eventName: string): void {
    try {
      window.POCKET.timeEvent(eventName);
    } catch (err: any) {
      SentryClient.captureError(err);
    }
  }

  static getRawUrl(): string {
    const { host, pathname } = window.location;
    return `${host}${pathname}`;
  }

  static trackPageView(additionalProperties: Record<string, unknown> = {}): void {
    this.trackEvent('Page View', {
      gt_Location: this.getRawUrl(),
      ...additionalProperties,
    });
  }

  static flagsBuilder(featureFlags?: { [key: string]: string | boolean }): {
    [key: string]: string | boolean;
  } {
    if (!featureFlags) return {};
    return {
      has_migrated_to_classrooms: featureFlags['redirect-to-classrooms'],
    };
  }

  static async registerIfIsInTrial() {
    if ((await Enrollments.isInTrial()) && (await Enrollments.isInTrialTracking())) {
      window.POCKET.registerMixpanel({ in_graduation_trial: true });
    } else {
      window.POCKET.unregisterMixpanel('in_graduation_trial');
    }
  }

  static async updateHubSpotContactPropertiesWithForm(
    userEmail: string,
    properties: Array<{ name: string; value: string | number }>,
    config: { hubSpotFormId: string },
  ) {
    if (!userEmail || !properties.length || !config.hubSpotFormId) return;

    const descomplicaHubSpotId = process.env.VUE_APP_HUBSPOT_ID;
    const hubSpotFormsApiEndpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${descomplicaHubSpotId}/${config.hubSpotFormId}`;

    const payload = { fields: [{ name: 'email', value: userEmail }, ...properties] };

    try {
      await axios.post(hubSpotFormsApiEndpoint, JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      SentryClient.captureError(err);
    }
  }
}

export default Analytics;
